import { Avatar, Box, Link, Theme, Tooltip } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import Chip from '@mui/material/Chip'
import React, { useState } from 'react'
import cx from 'classnames'

import { IChatMessage } from '../../../../../../api/types/chat'
import { useAuth } from '../../../../../../context/Auth'
import { ConfirmationDialog } from '../../../../../../components/ConfirmationDialog/ConfirmationDialog'
import { useMutation } from '@tanstack/react-query'
import { mutationDeleteChatEntry } from '../../../../../../api/mutations'
import { useGlobalError } from '../../../../../../context/GlobalError'
import { useChatEntryColorStyles } from './ChatEntryColors'
import { extractMessage } from '../../../../../../utils'
import { BACKEND_URL } from '../../../../../../config'
import { MarkdownComponent } from '../../../../../../components/Markdown/MarkdownComponent'
import DeleteIcon from '@mui/icons-material/Delete'
import { ChatItem } from '../ChatItem/ChatItem'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    delete: {
      zIndex: 100,
      position: 'absolute',
      right: '4px',
    },
    hidden: {
      display: 'none',
    },
    author: {
      fontWeight: 'bold',
    },
    entry: {
      '& img': {
        maxWidth: '600px',
        maxHeight: '600px',
      },
      display: 'flex',
      alignItems: 'flex-start',
      padding: '4px',
      /* color: theme.palette.mode === 'dark' ? '#d83dd3' : '#000000', */
      '& span': {
        '& *': {
          margin: 0,
        },
      },
    },
    entryWhisper: {
      fontStyle: 'italic',
      color: '#d83dd3',
    },
    message: {
      marginLeft: '8px',
    },
    userBadge: {
      fontWeight: 'bold',
    },
    userBadgeWhisper: {
      fontStyle: 'italic',
      fontWeight: 'bold',
      color: '#d83dd3',
      borderColor: '#d83dd3',
    },
    fromBox: {
      display: 'flex',
      alignItems: 'center',
    },
    othersWhisper: {
      opacity: '0.7',
    },
    blank: {
      display: 'none',
    },
    gmSayGlobal: {
      color: theme.palette.mode === 'dark' ? '#c7d047' : '#2d2597',
    },
    systemMessage: {
      color: theme.palette.mode === 'dark' ? '#8190a8' : '#7e6f57',
      fontStyle: 'italic',
    },
    roll: {
      color: theme.palette.mode === 'dark' ? '#8190a8' : '#7e6f57',
    },
    action: {
      color: 'red',
    },
  })
)

interface ChatEntryProps {
  entry: IChatMessage
  onImageLoad?: any
}

export const ChatMessageEntry: React.FC<ChatEntryProps> = ({ entry, onImageLoad }) => {
  const classes = useStyles()
  const classesColors = useChatEntryColorStyles()
  const auth = useAuth()
  const isWhisper: boolean = !!entry.whisper_to
  const isMeWhispering = entry.author === auth?.username && entry.whisper_to
  const isOthersWhisper =
    entry.whisper_to && entry.whisper_to !== auth?.username && entry.author !== auth?.username
  const isRoll = entry.message_type === 'roll'
  const isAction = entry.message_type === 'action'

  const username = isMeWhispering ? entry.whisper_to : entry.author
  const [deleteVisible, setDeleteVisible] = useState(false)
  const { addError } = useGlobalError()

  const { mutate: deleteEntryMutation } = useMutation(() => mutationDeleteChatEntry(entry.id), {
    onError: (err: any) => {
      addError(extractMessage(err) || 'Nie udało się usunąć wiadomości', err?.status || 500)
    },
    onSuccess: async () => setDeleteVisible(false),
  })

  const handleDeleteEntry = () => {
    deleteEntryMutation()
    return Promise.resolve()
  }

  const messageTypeToChipClass = {
    gm_global_say: classes.blank,
    system_whisper: classes.blank,
    whisper: classes.userBadgeWhisper,
    action: classes.action,
  }

  const messageTypeToMessageClass = {
    gm_global_say: classes.gmSayGlobal,
    system_whisper: classes.systemMessage,
    roll: classes.roll,
  }

  const messageColorToClass = {
    red: classesColors.msgColorRed,
    dw: classesColors.msgColorDwrf,
    gn: classesColors.msgColorGnom,
    sh: classesColors.msgColorShap,
    mo: classesColors.msgColorMorh,
    hu: classesColors.msgColorHum,
    el: classesColors.msgColorElf,
    or: classesColors.msgColorOrc,
    silver: classesColors.msgColorSilver,
    gold: classesColors.msgColorGold,
  }

  const getAvatar = (entry: IChatMessage) => {
    if (entry.avatar && !isWhisper) {
      return <Avatar alt="avatar" src={BACKEND_URL + entry.avatar} />
    }

    if (entry.whisper_to_avatar && isMeWhispering) {
      return <Avatar alt="avatar" src={BACKEND_URL + entry.whisper_to_avatar} />
    }
    if (entry.avatar && !isMeWhispering) {
      return <Avatar alt="avatar" src={BACKEND_URL + entry.avatar} />
    }

    return undefined
  }

  return (
    <Tooltip title={new Date(entry.created_at).toLocaleString('pl-PL')} arrow placement="top-end">
      <div
        onMouseEnter={() => setDeleteVisible(true)}
        onMouseLeave={() => setDeleteVisible(false)}
        className={cx(
          classes.entry,
          isWhisper && classes.entryWhisper,
          isOthersWhisper && classes.othersWhisper,
          // @ts-ignore
          messageTypeToMessageClass[entry.message_type],
          // @ts-ignore
          messageColorToClass[entry?.color]
        )}
      >
        <span className={classes.fromBox}>
          {isMeWhispering && <ArrowRightIcon fontSize="small" />}

          {/* //global chat user badge */}
          {!isWhisper && !isRoll && !isAction && (
            <Chip
              avatar={getAvatar(entry)}
              className={cx(
                // @ts-ignore
                messageTypeToChipClass[entry.message_type],
                classes.userBadge,
                // @ts-ignore
                messageColorToClass[entry?.color]
              )}
              variant="outlined"
              size="small"
              color="primary"
              label={username}
              // onClick={() => onUserBadgeClick(username)}
            />
          )}

          {/* //whisper user name badge */}
          {isWhisper && (
            <Chip
              avatar={getAvatar(entry)}
              // @ts-ignore
              className={cx(classes.userBadgeWhisper, classes.userBadge)}
              variant="outlined"
              size="small"
              color="primary"
              label={username}
              // onClick={() => onUserBadgeClick(username)}
            />
          )}

          {isOthersWhisper && (
            <>
              <ArrowRightIcon fontSize="small" />
              <Chip
                className={cx(entry.whisper_to ? classes.userBadgeWhisper : classes.userBadge)}
                variant="outlined"
                size="small"
                color="primary"
                label={entry.whisper_to}
                // onClick={() => onUserBadgeClick(entry.whisper_to)}
              />
            </>
          )}
        </span>
        <span className={cx(classes.message, 'chatEntry')}>
          {entry.message_type === 'item_display' || (
            <MarkdownComponent rawMarkdown={entry.message} onImageLoad={onImageLoad} />
          )}
          {entry.message_type === 'item_display' && (
            <ChatItem onLoad={onImageLoad} id={Number(entry.message)} />
          )}
        </span>
        {(auth?.admin || auth?.gm) && (
          <ConfirmationDialog
            body={
              <div>
                {'Delete entry?'}
                <div>
                  <MarkdownComponent rawMarkdown={entry.message} onImageLoad={onImageLoad} />
                </div>
              </div>
            }
          >
            {(confirm: any) => (
              <div className={cx(classes.delete, !deleteVisible && classes.hidden)}>
                <Link href="#" onClick={confirm((e: any) => () => handleDeleteEntry())}>
                  <DeleteIcon color={'secondary'} />
                </Link>
              </div>
            )}
          </ConfirmationDialog>
        )}
      </div>
    </Tooltip>
  )
}
